/**
 * Illustrative example artwork for the public site. Every image was generated for
 * Perzo's site (fictional subjects); none is a customer order or a provider benchmark.
 *
 * Diptych files hold the original photo in the left half and the art in the right
 * half, at identical framing, so the compare viewer can split them in CSS. Each
 * subject has exactly one real transformation; styles are never faked with filters.
 */
export type PieceKind = 'diptych' | 'single';

export interface Piece {
  id: string;
  subject: string;
  style: string;
  kind: PieceKind;
  /** Path without extension; the WebP derivative of root's PNG is what ships. */
  base: string;
  photoAlt: string;
  artAlt: string;
  /** How a merchant might brief this style, in their own words. */
  brief: string;
}

export const PIECES: Piece[] = [
  {
    id: 'portrait',
    subject: 'Portrait',
    style: 'Ink and wash',
    kind: 'diptych',
    base: '/images/perzo/person-ink',
    photoAlt: 'Example photo of a woman with curly hair, used as the original',
    artAlt: 'The same woman painted in deep blue ink washes with warm skin tones',
    brief: 'Blue ink and watercolor wash, loose hair strokes, warm skin, white paper ground.',
  },
  {
    id: 'couple',
    subject: 'Couple',
    style: 'Gouache',
    kind: 'diptych',
    base: '/images/perzo/couple-gouache',
    photoAlt: 'Example photo of a smiling couple outdoors, used as the original',
    artAlt: 'The same couple painted in broad gouache strokes of navy, sage and peach',
    brief: 'Gouache portrait, broad visible strokes, navy, sage and peach palette, white ground.',
  },
  {
    id: 'pet',
    subject: 'Pet',
    style: 'Watercolor',
    kind: 'diptych',
    base: '/images/perzo/pet-watercolor',
    photoAlt: 'Example photo of a golden retriever, used as the original',
    artAlt: 'The same golden retriever as a loose watercolor on white paper',
    brief: 'Loose watercolor on cold-press paper, warm light, visible washes, white ground.',
  },
];

/** A finished print in a room: what the production file becomes. */
export const STUDIO_PRINT: Piece = {
  id: 'studio',
  subject: 'Finished print',
  style: 'Framed',
  kind: 'single',
  base: '/images/perzo/studio-print',
  photoAlt: '',
  artAlt: 'A framed blue watercolor portrait leaning on a stone shelf in a sunlit studio',
  brief: '',
};

export const DIPTYCHS = PIECES.filter((piece) => piece.kind === 'diptych');

export function pieceById(id: string): Piece | undefined {
  return PIECES.find((piece) => piece.id === id);
}
